import React from 'react';
import CodeBlock from '../../CodeBlock';
import { FileText, AlertTriangle } from 'lucide-react';

const DocumentIngestion = () => {
  const BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api/v1';

  return (
    <div className="workflow-section">
      <div className="mb-8">
        <h1 className="text-4xl md:text-5xl font-black tracking-tight mb-4 text-white">Document Ingestion</h1>
        <p className="text-lg md:text-xl text-gray-300 leading-7 max-w-[65ch]">
          Upload and process documents for use in SOP generation. Documents are chunked, embedded, and made searchable
          through our semantic search pipeline.
        </p>
      </div>

      <div className="card mb-6 bg-gray-900 border border-gray-800">
        <h3 className="text-lg font-bold tracking-tight text-gray-200 mb-3">Overview</h3>
        <p className="text-sm text-gray-300 leading-6">
          Document ingestion automatically processes uploaded documents by chunking them, generating semantic embeddings,
          extracting entities and relationships for the Knowledge Graph, and indexing them for semantic search.
          Once processed, documents are ready for use in SOP generation and knowledge graph exploration.
        </p>
      </div>

      <div className="space-y-6">
        {/* Step 1: Prerequisites */}
        <div className="card">
          <div className="flex items-start gap-4 mb-6">
            <span className="flex-shrink-0 w-10 h-10 rounded-full bg-green-600 text-white flex items-center justify-center font-bold text-lg">1</span>
            <div className="flex-1">
              <h3 className="text-xl font-bold tracking-tight text-gray-200 mb-2">Ensure you have a namespace</h3>
              <p className="text-sm text-gray-400 leading-6">
                Documents must be uploaded to an existing namespace. If you don't have one, create it first.
              </p>
            </div>
          </div>

          <div className="ml-14">
            <CodeBlock code={`curl -X POST ${BASE_URL}/namespaces/ \\
  -H "X-API-Key: your-email@example.com:your_api_key_here" \\
  -H "Content-Type: application/json" \\
  -d '{
    "name": "my-documents",
    "description": "Namespace for document ingestion"
  }'`} />
          </div>
        </div>

        {/* Step 2: Upload Document */}
        <div className="card">
          <div className="flex items-start gap-4 mb-6">
            <span className="flex-shrink-0 w-10 h-10 rounded-full bg-green-600 text-white flex items-center justify-center font-bold text-lg">2</span>
            <div className="flex-1">
              <h3 className="text-xl font-bold tracking-tight text-gray-200 mb-2">Upload your document</h3>
              <p className="text-sm text-gray-400 leading-6">
                Upload a document using multipart/form-data. Supported formats include PDF, DOCX, TXT, and Markdown.
              </p>
            </div>
          </div>

          <div className="ml-14 space-y-4">
            <div>
              <h4 className="text-sm font-semibold text-gray-300 mb-2">Request</h4>
              <CodeBlock code={`curl -X POST ${BASE_URL}/namespaces/{namespace_uuid}/documents \\
  -H "X-API-Key: your-email@example.com:your_api_key_here" \\
  -F "file=@document.pdf"`} />
            </div>

            <div>
              <h4 className="text-sm font-semibold text-gray-300 mb-2">Response</h4>
              <CodeBlock code={`{
  "document_uuid": "b2c3d4e5-f6a7-8901-bcde-f12345678901",
  "ingestion_task_uuid": "c3d4e5f6-a7b8-9012-cdef-123456789012",
  "status": "PROCESSING",
  "filename": "document.pdf",
  "namespace_uuid": "{namespace_uuid}",
  "created_at": "2024-01-15T10:00:00Z"
}`} />
            </div>

            <div className="p-4 bg-blue-900/20 border border-blue-700/50 rounded-lg">
              <p className="text-sm text-blue-200 flex items-start gap-2">
                <FileText className="w-4 h-4 mt-0.5 flex-shrink-0" />
                <span>
                  <strong className="font-semibold">Supported formats:</strong> PDF, DOCX, TXT, MD (Markdown).
                  Maximum file size is typically 10MB, though this may vary by configuration.
                </span>
              </p>
            </div>
          </div>
        </div>

        {/* Step 3: Monitor Ingestion */}
        <div className="card">
          <div className="flex items-start gap-4 mb-6">
            <span className="flex-shrink-0 w-10 h-10 rounded-full bg-green-600 text-white flex items-center justify-center font-bold text-lg">3</span>
            <div className="flex-1">
              <h3 className="text-xl font-bold tracking-tight text-gray-200 mb-2">Monitor ingestion progress</h3>
              <p className="text-sm text-gray-400 leading-6">
                Check the ingestion task status to know when your document is ready. The process is asynchronous and typically takes 30 seconds to 2 minutes.
              </p>
            </div>
          </div>

          <div className="ml-14 space-y-4">
            <div>
              <h4 className="text-sm font-semibold text-gray-300 mb-2">Request</h4>
              <CodeBlock code={`curl -X GET ${BASE_URL}/ingestionTasks/{ingestion_task_uuid} \\
  -H "X-API-Key: your-email@example.com:your_api_key_here"`} />
            </div>

            <div>
              <h4 className="text-sm font-semibold text-gray-300 mb-2">Response (Processing)</h4>
              <CodeBlock code={`{
  "uuid": "{ingestion_task_uuid}",
  "status": "PROCESSING",
  "document_uuid": "{document_uuid}",
  "namespace_uuid": "{namespace_uuid}",
  "progress": 0.6,
  "message": "Embedding document chunks..."
}`} />
            </div>

            <div>
              <h4 className="text-sm font-semibold text-gray-300 mb-2">Response (Completed)</h4>
              <CodeBlock code={`{
  "uuid": "{ingestion_task_uuid}",
  "status": "COMPLETED",
  "document_uuid": "{document_uuid}",
  "namespace_uuid": "{namespace_uuid}",
  "progress": 1.0,
  "message": "Document ingestion completed successfully",
  "num_chunks": 45,
  "created_at": "2024-01-15T10:00:00Z",
  "completed_at": "2024-01-15T10:01:30Z"
}`} />
            </div>

            <div className="p-4 bg-yellow-900/20 border border-yellow-700/50 rounded-lg">
              <div className="flex items-start gap-2">
                <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0 text-yellow-400" />
                <div className="flex-1">
                  <p className="text-sm text-yellow-200">
                    <strong className="font-semibold">Status values:</strong>
                    <ul className="mt-2 space-y-1 list-disc list-inside ml-2">
                      <li><code className="bg-yellow-900/50 px-1 py-0.5 rounded">PROCESSING</code> - Document is being processed</li>
                      <li><code className="bg-green-900/50 px-1 py-0.5 rounded">COMPLETED</code> - Document is ready for use</li>
                      <li><code className="bg-red-900/50 px-1 py-0.5 rounded">FAILED</code> - Processing failed (check message field)</li>
                    </ul>
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Best Practices */}
        <div className="card mt-8 bg-gray-900 border border-gray-800">
          <h3 className="text-lg font-bold tracking-tight text-gray-200 mb-3">Best Practices</h3>
          <div className="space-y-3 text-sm text-gray-300">
            <div>
              <strong className="text-gray-200">File organization:</strong>
              <p className="text-gray-400 mt-1">
                Group related documents in the same namespace. This helps the SOP generation process understand context and relationships between documents.
              </p>
            </div>
            <div>
              <strong className="text-gray-200">Document quality:</strong>
              <p className="text-gray-400 mt-1">
                Use clear, well-structured documents. Documents with headings, lists, and clear sections produce better SOP results.
              </p>
            </div>
            <div>
              <strong className="text-gray-200">Bulk uploads:</strong>
              <p className="text-gray-400 mt-1">
                Upload multiple documents to build a comprehensive knowledge base. More documents typically lead to higher-quality SOPs.
              </p>
            </div>
            <div>
              <strong className="text-gray-200">Polling strategy:</strong>
              <p className="text-gray-400 mt-1">
                Poll the ingestion task endpoint every 5-10 seconds. Don't poll too frequently—give the system time to process.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DocumentIngestion;
